// Drag-to-assign orchestration (WORK-6034): the shared drag ghost and the in-session override
// overlay + drop plan/run sequencing. The worker↔open-slot eligibility rule lives in
// shift-eligibility.ts. Client-side until the balancer exposes a drop-validity API and committed
// assignments.
import React from 'react';
import { Colors } from '@common/tokens';
import type { ApolloError } from '@common/hooks';
import type {
  AssignShiftMutationCgResponse,
  AssignShiftMutationVariables,
  UnassignShiftMutationVariables,
} from '@common/graphql';
import type {
  ScheduleDragStateType,
  ScheduleShiftOverrideType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';

const GHOST_OFFSET = 16;

function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Builds the shared drag image (avatar initials + name) and wires it as the drag ghost, offset from
 * the cursor. The detached node is removed once the browser has snapshotted it for the drag image.
 */
export function startDragGhost(event: React.DragEvent, name: string): void {
  event.dataTransfer.effectAllowed = 'move';

  const ghost = document.createElement('div');
  Object.assign(ghost.style, {
    position: 'absolute',
    top: '-1000px',
    left: '-1000px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px 6px 6px',
    borderRadius: '999px',
    background: Colors.white,
    border: `1px solid ${Colors.steel}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
    font: '500 13px system-ui, sans-serif',
    color: Colors.textDefault,
    whiteSpace: 'nowrap',
  });

  const avatar = document.createElement('span');
  Object.assign(avatar.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: Colors.steelLighter,
    color: Colors.midnightRegular,
    fontSize: '11px',
  });
  avatar.textContent = initialsOf(name);

  const label = document.createElement('span');
  label.textContent = name;

  ghost.append(avatar, label);
  document.body.appendChild(ghost);
  event.dataTransfer.setDragImage(ghost, GHOST_OFFSET, GHOST_OFFSET);
  requestAnimationFrame(() => ghost.remove());
}

/**
 * Layers in-session assignment overrides onto the base shifts so the grid reflects manual assigns,
 * unassigns and reviewed Works suggestions:
 * - an assigned member id sets the worker (as a manual source);
 * - an explicit null vacates the slot back to open;
 * - an accepted suggestion confirms the suggested worker (clears the pending flag);
 * - a rejected suggestion vacates the slot back to open.
 */
export function applyShiftOverrides(
  shifts: ScheduleShiftType[],
  overrides: Record<string, ScheduleShiftOverrideType>,
  members: ScheduleTeamMemberType[]
): ScheduleShiftType[] {
  const nameById = new Map(members.map((member) => [member.id, member.name]));
  const vacate = (shift: ScheduleShiftType): ScheduleShiftType => ({
    ...shift,
    workerName: undefined,
    source: shift.source === 'pto' ? shift.source : 'open',
    suggested: undefined,
  });

  return shifts.map((shift) => {
    const override = overrides[shift.id];
    if (!override) return shift;

    if (override.assignedMemberId) {
      return {
        ...shift,
        workerName: nameById.get(override.assignedMemberId) ?? shift.workerName,
        source: 'manual',
        suggested: undefined,
      };
    }

    if (override.assignedMemberId === null) return vacate(shift);

    if (override.suggestionDecision === 'accepted') {
      return { ...shift, suggested: undefined };
    }

    if (override.suggestionDecision === 'rejected') return vacate(shift);

    return shift;
  });
}

// ── Drag-to-assign orchestration (WORK-6399) ─────────────────────────────────────
// Wires the three drag sources onto the real assign/unassign mutations. A `grid` source
// carries its origin shift — a MOVE (unassign the source, then assign the target); a
// `fit`/`roster` source carries none — a pure ASSIGN. Split into a pure `planDragDrop`
// (decide what to do) and an effectful `runDragDrop` (do it, reconcile the optimistic
// overlay, and RETURN what happened) so both are unit-testable without rendering the grid.
// The toast is deliberately NOT raised here: `runDragDrop` returns a `DragDropOutcomeType`
// and the calling hook maps it to a single user-facing message — a move must surface one
// "Worker moved." toast, not the two button toasts its two legs would otherwise fire, and
// keeping the copy at the React layer leaves this service free of presentation deps.

type OpenCellParserType = (shiftId: string | null) => {
  weekKey: string;
  dayKey: string;
  shiftTypeSection: string;
  role: string;
} | null;

/** Prior values of the involved override keys, captured before the optimistic patch. */
export type ShiftOverrideSnapshotType = Record<
  string,
  ScheduleShiftOverrideType | undefined
>;

/** The action a drop resolves to: nothing, a pure assign, or a move (unassign then assign). */
export type ScheduleDragDropPlanType =
  | { kind: 'noop' }
  | {
      kind: 'assign';
      toShiftId: string;
      workerId: string;
      assignVars: AssignShiftMutationVariables;
    }
  | {
      kind: 'move';
      fromShiftId: string;
      toShiftId: string;
      workerId: string;
      unassignVars: UnassignShiftMutationVariables;
      assignVars: AssignShiftMutationVariables;
    };

/**
 * Resolves a drop onto an open cell into the action to run. Pure — every guard lives here so
 * {@link runDragDrop} only sequences mutations. The target is always an open cell, addressed by
 * the grid descriptor encoded in `toShiftId` (parsed by the injected `parseOpenShiftId`; injected
 * rather than imported because it lives in the page-private service). Returns `noop` when there is
 * nothing safe to do: no drag, missing run/unit, an unparseable target, or a same-cell drop. A
 * `fromShiftId` on the drag means MOVE (unassign it first); its absence means ASSIGN — so dragging
 * an already-assigned worker from a candidate/team row is an add, not an implicit move.
 */
export function planDragDrop(
  dragState: ScheduleDragStateType,
  toShiftId: string,
  ctx: {
    runId: string | undefined;
    organizationUnitId: string | undefined;
    parseOpenShiftId: OpenCellParserType;
  }
): ScheduleDragDropPlanType {
  const { runId, organizationUnitId, parseOpenShiftId } = ctx;
  if (!dragState || !runId || !organizationUnitId) return { kind: 'noop' };

  const cell = parseOpenShiftId(toShiftId);
  if (!cell) return { kind: 'noop' };

  const { workerId, fromShiftId } = dragState;
  if (fromShiftId === toShiftId) return { kind: 'noop' };

  // Drag-to-assign is a power-user, free-form action (WORK-6399): force past the server's
  // eligibility gate (role is still enforced). Set on both the pure-assign and the move's
  // target-assign so a drop never fails on an FTE/hours/overlap/preference block. The panel
  // Assign button builds its own vars without force, so it stays eligible-only (WORK-6384).
  const assignVars: AssignShiftMutationVariables = {
    balancerRunId: runId,
    organizationUnitId,
    weekKey: cell.weekKey,
    dayKey: cell.dayKey,
    shiftTypeSection: cell.shiftTypeSection,
    role: cell.role,
    workerId,
    force: true,
  };

  if (fromShiftId) {
    return {
      kind: 'move',
      fromShiftId,
      toShiftId,
      workerId,
      unassignVars: {
        balancerRunId: runId,
        organizationUnitId,
        shiftId: fromShiftId,
      },
      assignVars,
    };
  }

  return { kind: 'assign', toShiftId, workerId, assignVars };
}

/**
 * Injected effects for {@link runDragDrop} — the (toast-silent) mutate fns and the override-overlay
 * setters — so the orchestration is unit-testable with `jest.fn()`. The toast itself is NOT injected:
 * `runDragDrop` returns the outcome and the caller raises the single message (see header note).
 */
export type RunDragDropDepsType = {
  /**
   * Toast-silent assign mutate fn; rejects on a mutation error (drives rollback + sequencing).
   * Resolves to the Apollo result so the caller can read the inserted shift's uuid.
   */
  assign: (
    variables: AssignShiftMutationVariables
  ) => Promise<{ data?: AssignShiftMutationCgResponse | null } | undefined>;
  /** Toast-silent unassign mutate fn; rejects on a mutation error. */
  unassign: (variables: UnassignShiftMutationVariables) => Promise<unknown>;
  /** Capture the involved override keys' current values before patching (for rollback). */
  snapshotOverrides: (shiftIds: string[]) => ShiftOverrideSnapshotType;
  patchOverride: (shiftId: string, patch: ScheduleShiftOverrideType) => void;
  /** Restore a snapshot — re-set captured keys, delete the keys that were absent. */
  restoreOverrides: (snapshot: ShiftOverrideSnapshotType) => void;
  /** Delete override keys (the refetched server truth now drives those cells). */
  clearOverrides: (shiftIds: string[]) => void;
};

/**
 * What a drop ended up doing, for the caller to map to a single toast. Successes (`assigned` /
 * `moved`) carry no payload; the failure variants carry the {@link ApolloError} so the caller
 * resolves the per-code copy (`move-assign-error` has fixed copy — the partial-move outcome).
 */
export type DragDropOutcomeType =
  | { kind: 'noop' }
  // `affectedShiftId` is the newly-filled shift's uuid, so the caller can keep an open Shift
  // Detail panel pointed at the slot it just filled (the open-row id it was keyed on vanishes
  // on refetch). Undefined if the mutation response omitted it.
  | { kind: 'assigned'; affectedShiftId?: string }
  | { kind: 'moved'; affectedShiftId?: string }
  | { kind: 'assign-error'; error: ApolloError }
  | { kind: 'unassign-error'; error: ApolloError }
  | { kind: 'move-assign-error' };

/**
 * Runs the planned drop with an optimistic overlay: snapshot → patch → mutate → reconcile, and
 * returns what happened. A move sequences unassign-first (never double-books; frees the source FTE
 * so the assign passes eligibility); the assign's blocking `awaitRefetchQueries` then reconciles
 * server truth. On success the involved overrides are cleared (the refetched grid drives those
 * cells). On a mutation failure the snapshot is restored — except the move partial-failure (unassign
 * committed, assign failed), where the worker is correctly left unassigned: the source override is
 * cleared (the unassign refetch already shows it open) and only the target is reverted. The caller
 * turns the returned outcome into the single user-facing toast.
 */
export async function runDragDrop(
  plan: ScheduleDragDropPlanType,
  deps: RunDragDropDepsType
): Promise<DragDropOutcomeType> {
  if (plan.kind === 'noop') return { kind: 'noop' };

  if (plan.kind === 'assign') {
    const snapshot = deps.snapshotOverrides([plan.toShiftId]);
    deps.patchOverride(plan.toShiftId, {
      assignedMemberId: plan.workerId,
      suggestionDecision: undefined,
    });
    try {
      const result = await deps.assign(plan.assignVars);
      const affectedShiftId = result?.data?.assignShift?.affectedShiftId;
      deps.clearOverrides([plan.toShiftId]);
      return { kind: 'assigned', affectedShiftId };
    } catch (error) {
      deps.restoreOverrides(snapshot);
      return { kind: 'assign-error', error: error as ApolloError };
    }
  }

  const snapshot = deps.snapshotOverrides([plan.fromShiftId, plan.toShiftId]);
  deps.patchOverride(plan.fromShiftId, { assignedMemberId: null });
  deps.patchOverride(plan.toShiftId, {
    assignedMemberId: plan.workerId,
    suggestionDecision: undefined,
  });

  try {
    await deps.unassign(plan.unassignVars);
  } catch (error) {
    // Nothing committed — fully revert both legs.
    deps.restoreOverrides(snapshot);
    return { kind: 'unassign-error', error: error as ApolloError };
  }

  try {
    const result = await deps.assign(plan.assignVars);
    const affectedShiftId = result?.data?.assignShift?.affectedShiftId;
    deps.clearOverrides([plan.fromShiftId, plan.toShiftId]);
    return { kind: 'moved', affectedShiftId };
  } catch {
    // Unassign committed but assign failed: the worker is correctly left unassigned (never
    // double-booked). The unassign refetch already shows the source open, so clear its override;
    // revert only the target. The caller surfaces distinct copy — the generic assign error would
    // wrongly imply nothing happened.
    deps.clearOverrides([plan.fromShiftId]);
    deps.restoreOverrides({ [plan.toShiftId]: snapshot[plan.toShiftId] });
    return { kind: 'move-assign-error' };
  }
}
